import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function AIUseDisclosure() {
  return (
    <LegalLayout
      slug="ai-use"
      eyebrow="AI"
      title="AI Use Disclosure"
      lastUpdated="May 26, 2026 · Reviewed by counsel"
    >
      <p>
        This disclosure explains where Armstrong K9 Registry
        uses artificial-intelligence ("AI") and large-language-model ("LLM")
        systems inside the platform, what data is sent to those systems, what
        you can opt out of, and what warranties do (and do not) attach to
        AI-generated output.
      </p>

      <h2>1. Where AI is used today</h2>

      <h3>1.1 Marketing Refresh (admin-only)</h3>
      <p>
        Admin operators can trigger the "Marketing refresh" tool inside the
        Admin Console. It sends live aggregate stats (kennel count, dog count,
        breed count, registered litters, completed transports, override-aware
        pricing) plus a system prompt to <strong>Anthropic Claude Sonnet 4.5</strong>{" "}
        via the Emergent Universal LLM Key. The model returns a marketing pack
        (pitch / press release / tweet thread / LinkedIn / video script /
        emails / investor one-pager). Output is reviewed by the admin before
        any external use. No end-user PII is sent.
      </p>

      <h3>1.2 Predicted whelp date (breeder-facing, opt-in)</h3>
      <p>
        When a breeder logs a breeding date in the calendar, the platform
        offers to auto-create a tentative whelp date <strong>63 days later</strong>{" "}
        (the average canine gestation length). This is a deterministic
        arithmetic calculation — not an AI prediction — but is flagged here
        for transparency because the calendar surface is otherwise event-
        driven.
      </p>

      <h3>1.3 Pitch Kit (admin-only, static)</h3>
      <p>
        The Pitch Kit at <code>/admin/pitch-kit</code> uses pre-authored
        copy (no live LLM calls). It is included here only to clarify that
        no per-visit AI processing happens on that page.
      </p>

      <h2>2. What is NOT AI</h2>
      <p>
        The following features are sometimes mistaken for AI but are
        deterministic algorithms running on your own data:
      </p>
      <ul>
        <li>Wright's Coefficient of Inbreeding (COI) — pure pedigree arithmetic.</li>
        <li>Match-score on breeding listings — rule-based weighting.</li>
        <li>Predicted next-heat date — last-heat-plus-180-days arithmetic.</li>
        <li>Predicted whelp date — breeding-date-plus-63-days arithmetic.</li>
        <li>Symptom warnings inside the Dog Log — keyword + threshold rules.</li>
      </ul>

      <h2>3. What we send to the LLM (and what we do not)</h2>
      <p>For the one live LLM feature (Marketing Refresh) we send:</p>
      <ul>
        <li>System prompt (brand voice + hard constraints)</li>
        <li>Aggregate platform statistics (counts only, no individual records)</li>
        <li>Override-aware pricing table</li>
        <li>Recent changelog excerpt</li>
        <li>Geography metadata</li>
      </ul>
      <p>We do <strong>not</strong> send:</p>
      <ul>
        <li>Any individual user's name, email, or contact details</li>
        <li>Dog records, photos, pedigrees, or registration numbers</li>
        <li>Buyer / seller messages or inbox content</li>
        <li>Payment data of any kind</li>
        <li>Care logs, vet records, or sitter share data</li>
      </ul>

      <h2>4. Training-data posture</h2>
      <p>
        We use Anthropic's API via the Emergent Universal Key. Anthropic
        does <strong>not</strong> train its production models on API
        traffic by default. We do not opt into any training-data sharing
        program. See Anthropic's API privacy posture for the most current
        statement.
      </p>

      <h2>5. Warranty disclaimer for AI output</h2>
      <p>
        AI-generated output is <strong>probabilistic, not deterministic</strong>.
        We make no warranty that AI output is accurate, free of factual
        errors, fit for any particular purpose, or compliant with any
        regulatory requirement in your jurisdiction. Admin operators are
        responsible for reviewing every AI output before external use.
      </p>
      <p>
        This applies in particular to:
      </p>
      <ul>
        <li>Marketing claims (verify against live platform state)</li>
        <li>Press release facts (verify against ground truth)</li>
        <li>Email copy (review tone + compliance before sending)</li>
      </ul>

      <h2>6. Opting out</h2>
      <ul>
        <li>
          <strong>End users:</strong> the one live LLM feature is admin-only,
          so end-user activity is never sent to an LLM. No opt-out is needed
          on your part.
        </li>
        <li>
          <strong>Admins:</strong> simply do not click "Generate" on the
          Marketing Refresh page. There are no background or automatic LLM
          calls.
        </li>
      </ul>

      <h2>7. Future AI features</h2>
      <p>
        New AI features will be disclosed on this page, in the relevant
        sub-processor list update, and with explicit consent UX where the
        feature touches end-user data. We will not back-port AI processing
        to existing end-user data without a fresh consent step.
      </p>

      <h2>8. Contact</h2>
      <p>
        AI / data-protection questions:{" "}
        <a href="mailto:privacy@armstrongk9registry.com">privacy@armstrongk9registry.com</a> or
        use our <a href="/contact">Contact Us</a> form.
      </p>
    </LegalLayout>
  );
}
